// Web-Push im Browser.
//
// Auf iOS funktioniert das ausschließlich in der zum Home-Bildschirm
// hinzugefügten PWA – das wird in den Einstellungen auch so gesagt, statt
// einen Knopf anzubieten, der wortlos nichts tut.
import { api } from './api.js';

export interface PushConfig {
  configured: boolean;
  publicKey: string;
  devices: number;
  prefs: Record<string, boolean>;
  quietHours: string | null;
  kinds: { key: string; label: string }[];
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Läuft die App als installierte PWA? Auf iOS Voraussetzung für Push. */
export function isStandalone(): boolean {
  return (
    matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  return /iP(hone|ad|od)/.test(navigator.userAgent);
}

/** Erklärt, warum Push hier (noch) nicht geht – oder null, wenn es geht. */
export function pushBlockedReason(config: PushConfig): string | null {
  if (!config.configured) {
    return 'Auf dem Server fehlen die VAPID-Schlüssel (npm run gen:vapid).';
  }
  if (!pushSupported()) return 'Dieser Browser unterstützt keine Benachrichtigungen.';
  if (isIos() && !isStandalone()) {
    return 'Auf dem iPhone gibt es Mitteilungen nur für die installierte App: Teilen → „Zum Home-Bildschirm".';
  }
  if (Notification.permission === 'denied') {
    return 'Mitteilungen wurden für diese Seite blockiert – in den Browser-Einstellungen wieder erlauben.';
  }
  return null;
}

export function getConfig(): Promise<PushConfig> {
  return api<PushConfig>('/api/push/config');
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export async function subscribe(publicKey: string): Promise<boolean> {
  const registration = await navigator.serviceWorker.ready;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    }));

  const json = subscription.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return false;

  await api('/api/push/subscribe', {
    method: 'POST',
    body: { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } },
  });
  return true;
}

export async function unsubscribe(): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await api('/api/push/unsubscribe', {
    method: 'POST',
    body: { endpoint: subscription.endpoint },
  });
  await subscription.unsubscribe();
}

export async function isSubscribed(): Promise<boolean> {
  if (!pushSupported()) return false;
  const registration = await navigator.serviceWorker.ready;
  return Boolean(await registration.pushManager.getSubscription());
}

export function savePrefs(body: {
  prefs?: Record<string, boolean>;
  quietHours?: string | null;
}): Promise<unknown> {
  return api('/api/push/prefs', { method: 'PATCH', body });
}

export function sendTest(): Promise<{ sent: number }> {
  return api('/api/push/test', { method: 'POST' });
}
